import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaSearch, FaTimes, FaArrowRight, FaHome, FaInfoCircle, FaCreditCard, FaPiggyBank, FaHandHoldingUsd, FaEnvelope, FaLock, FaFileAlt } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: flex-start;
  padding-top: 10vh;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
  overflow-y: auto;
`;

const SearchContainer = styled.div`
  background: #ffffff;
  padding: 0;
  width: 90%;
  max-width: 600px;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  text-align: left;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
  margin-bottom: 50px;
`;

const SearchHeader = styled.div`
  padding: 24px 24px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gn-color-text, #1B1833);
  font-family: var(--gn-font-heading);
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: var(--gn-color-primary, #821111);
    transform: rotate(90deg);
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const SearchInputIconWrapper = styled.div`
  position: absolute;
  left: 36px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 1.1rem;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 48px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f8f9fa;
  color: var(--gn-color-text, #1B1833);
  font-family: var(--gn-font-body);

  &:focus {
    outline: none;
    border-color: var(--gn-color-primary, #821111);
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(130, 17, 17, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;


const ResultsContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
`;

const SearchResult = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  margin: 4px 0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  border: 1px solid transparent;

  &:hover {
    background: linear-gradient(90deg, rgba(130, 17, 17, 0.05) 0%, rgba(130, 17, 17, 0.02) 100%);
    border-color: rgba(130, 17, 17, 0.1);
    transform: translateX(4px);
  }

  &:active {
    transform: translateX(2px);
  }
`;

const ResultContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const ResultIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--gn-color-primary, #821111) 0%, rgba(130, 17, 17, 0.8) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const ResultText = styled.div`
  flex: 1;
`;

const ResultName = styled.div`
  font-weight: 600;
  color: var(--gn-color-text, #1B1833);
  font-size: 0.95rem;
  margin-bottom: 2px;
`;

const ResultPath = styled.div`
  font-size: 0.8rem;
  color: #999;
  font-family: var(--gn-font-body);
`;

const ResultArrow = styled.div`
  color: #ccc;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  opacity: 0;

  ${SearchResult}:hover & {
    opacity: 1;
    color: var(--gn-color-primary, #821111);
    transform: translateX(4px);
  }
`;

const NoResults = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: #999;
`;

const NoResultsIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 12px;
  opacity: 0.3;
`;

const NoResultsText = styled.div`
  font-size: 1rem;
  font-weight: 500;
`;

const SearchHint = styled.div`
  padding: 12px 24px;
  background: rgba(130, 17, 17, 0.05);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--gn-color-primary, #821111) !important;
  font-size: 1.0rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--gn-color-accent, #D39D55) !important;
  }

  svg {
    display: block;
  }
`;

// Icon mapping for different page types
const getPageIcon = (path) => {
  if (path === '/') return FaHome;
  if (path.includes('about')) return FaInfoCircle;
  if (path.includes('checking') || path.includes('credit')) return FaCreditCard;
  if (path.includes('savings')) return FaPiggyBank;
  if (path.includes('loan')) return FaHandHoldingUsd;
  if (path.includes('contact')) return FaEnvelope;
  if (path.includes('privacy') || path.includes('terms')) return FaLock;
  return FaFileAlt;
};

const SearchOverlay = ({ label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const searchData = [
    { name: 'Home', path: '/', category: 'Main' },
    { name: 'About Us', path: '/about', category: 'Company' },
    { name: 'Checking Accounts', path: '/checking', category: 'Banking' },
    { name: 'Savings Accounts', path: '/savings', category: 'Banking' },
    { name: 'Loan Services', path: '/loans', category: 'Products' },
    { name: 'Credit Cards', path: '/credit-cards', category: 'Products' },
    { name: 'Business Banking', path: '/business', category: 'Business' },
    { name: 'Contact', path: '/contact', category: 'Support' },
    { name: 'Privacy Policy', path: '/privacy', category: 'Legal' },
    { name: 'Terms & Conditions', path: '/terms', category: 'Legal' },
    { name: 'Fees Disclosure', path: '/fees-disclosure', category: 'Legal' },
    { name: 'Online Banking', path: '/OnlineBanking', category: 'Banking' },
    { name: 'Mobile Banking', path: '/MobileBanking', category: 'Banking' },
    { name: 'E-Bill Pay', path: '/EBilling', category: 'Banking' },
    { name: 'Location', path: '/location', category: 'Support' },
  ];

  const filteredResults = searchData.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleResultClick = (path) => {
    navigate(path);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
      if (e.key === 'Enter' && filteredResults.length > 0 && searchTerm) {
        handleResultClick(filteredResults[0].path);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, filteredResults, searchTerm]);

  return (
    <>
      {label ? (
        <button
          type="button"
          className="gn-utility-trigger"
          onClick={() => setIsOpen(true)}
          aria-label="Search"
        >
          {label}
        </button>
      ) : (
        <SearchIconWrapper onClick={() => setIsOpen(true)} aria-label="Search">
          <FaSearch />
        </SearchIconWrapper>
      )}

      <Overlay isOpen={isOpen} onClick={handleClose}>
        <SearchContainer onClick={(e) => e.stopPropagation()}>
          <SearchHeader>
            <SearchTitle>Search</SearchTitle>
            <CloseButton onClick={handleClose} aria-label="Close search">
              <FaTimes />
            </CloseButton>
          </SearchHeader>

          <SearchInputWrapper>
            <SearchInputIconWrapper>
              <FaSearch />
            </SearchInputIconWrapper>
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder="Search pages, services, or information..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </SearchInputWrapper>

          {searchTerm && (
            <ResultsContainer>
              {filteredResults.length > 0 ? (
                filteredResults.map((result) => {
                  const IconComponent = getPageIcon(result.path);
                  return (
                    <SearchResult
                      key={result.path}
                      onClick={() => handleResultClick(result.path)}
                    >
                      <ResultContent>
                        <ResultIcon>
                          <IconComponent />
                        </ResultIcon>
                        <ResultText>
                          <ResultName>{result.name}</ResultName>
                          <ResultPath>{result.category} • {result.path}</ResultPath>
                        </ResultText>
                      </ResultContent>
                      <ResultArrow>
                        <FaArrowRight />
                      </ResultArrow>
                    </SearchResult>
                  );
                })
              ) : (
                <NoResults>
                  <NoResultsIcon>
                    <FaSearch />
                  </NoResultsIcon>
                  <NoResultsText>No results found for "{searchTerm}"</NoResultsText>
                </NoResults>
              )}
            </ResultsContainer>
          )}

          {!searchTerm && (
            <NoResults>
              <NoResultsIcon>
                <FaSearch />
              </NoResultsIcon>
              <NoResultsText>Start typing to search...</NoResultsText>
            </NoResults>
          )}

          <SearchHint>
            <FaInfoCircle />
            Press <kbd>ESC</kbd> to close • <kbd>Enter</kbd> to select first result
          </SearchHint>
        </SearchContainer>
      </Overlay>
    </>
  );
};

export default SearchOverlay;
